/* global runtime, mergeState, safeArray, num, isoToNb, derivePayroll, getReportsModel, buildLedgerLines, getPayrollPayTypeDefinition, payrollLinePensionContribution, isPostedPayrollRun */
// LØNNSAVSTEMMING — lesemodell for byråets lønnskjerm
//
// Lønn er det området der saldoen alene sier minst. 2600 kan gå opp mot seg
// selv og likevel være feil: grunnlaget ligger i lønnskjøringene, det som er
// meldt ligger i A-meldingen, og det som faktisk er betalt ligger i banken.
// Fire kilder, ikke én saldo.
//
// Modellen er READ-ONLY og regner ikke lønn på nytt. Tallene er lønnskjøringenes
// egne (`derivePayroll` over `state.payrollRuns`), hovedboken kommer fra samme
// saldobalanse som resten av avstemmingen, og A-meldingsstatusen er den appen
// selv har registrert. Regnet denne skjermen ut sin egen arbeidsgiveravgift,
// ville avstemmingen avstemt mot seg selv.
//
// Der appen ikke VET noe, sier modellen det i stedet for å gjette. A-melding
// sendes i dag utenfor Plumme og registreres manuelt (`altinnSent`), så
// avstemmingen kan bekrefte at en kjøring er meldt — ikke hva Altinn mottok.

const payReconMonthRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex)) return null;
  const pad = (value) => String(value).padStart(2, '0');
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return {
    year, monthIndex,
    from: `${year}-${pad(monthIndex + 1)}-01`,
    to: `${year}-${pad(monthIndex + 1)}-${pad(lastDay)}`,
  };
};

const payReconRound = (value) => Math.round((Number(value) || 0) * 100) / 100;

// Kjøringene som hører til perioden. `run.period` er den avgjørende: en kjøring
// for august som utbetales 2. september er fortsatt augustlønn, og skal
// avstemmes i august. Utbetalingsdatoen brukes bare når perioden mangler.
const payReconRunsInPeriod = (runs, range, periodId) => safeArray(runs).filter((run) => {
  const period = String(run?.period || '').slice(0, 7);
  if (period) return period === String(periodId);
  const paid = String(run?.paymentDate || '').slice(0, 10);
  return Boolean(paid) && paid >= range.from && paid <= range.to;
});

// Lønnsartene deler seg i tre grupper avstemmingen faktisk spør om: hva som er
// natural­ytelse, hva som er trekkfri godtgjørelse, og hva som er trekk. Delingen
// er lønnsartens egen (`kind`), ikke en ny regel oppfunnet her.
const payReconItemKind = (item, settings) => {
  const definition = getPayrollPayTypeDefinition(item?.payType, settings);
  return definition?.kind || 'Lønnstype';
};

const payReconLineTotals = (line, settings) => {
  const items = safeArray(line?.items);
  const sumWhere = (test) => items.filter(test).reduce((sum, item) => sum + num(item?.amount), 0);
  return {
    gross: num(line?.grossSalary),
    benefits: sumWhere((item) => payReconItemKind(item, settings) === 'Naturalytelse'),
    allowances: sumWhere((item) => payReconItemKind(item, settings) === 'Trekkfri godtgjørelse'),
    // Trekkpliktige ytelser er de trekkene som selv er skattepliktige — de
    // øker forskuddstrekket uten å være vanlig lønn.
    taxableDeductions: sumWhere((item) => payReconItemKind(item, settings) === 'Trekk' && item?.taxable),
    tax: num(line?.taxDeduction),
    deductions: num(line?.deductions),
    agaBase: items.filter((item) => item?.employerContribution).reduce((sum, item) => sum + num(item?.grossSalary), 0),
    aga: num(line?.employerContribution),
    net: num(line?.netPay),
    holidayAccrual: num(line?.holidayPayAccrual),
    holidayPayout: num(line?.holidayPayPayout),
    holidayBase: num(line?.holidayPayBase),
    pension: payrollLinePensionContribution(line, settings),
    reimbursement: num(line?.expenseReimbursement),
  };
};

const payReconSumTotals = (rows) => rows.reduce((sum, row) => {
  const next = { ...sum };
  for (const key of Object.keys(row)) next[key] = payReconRound((next[key] || 0) + row[key]);
  return next;
}, {});

// Rollen en hovedbokskonto spiller i lønnsavstemmingen. Kodene er appens egne
// posteringskoder (se `payrollPostingRows`), ikke et generisk kontoplanoppslag:
// det er de kontoene lønnen faktisk føres mot i denne appen.
const payReconAccountRole = (code) => {
  const text = String(code || '');
  if (text.startsWith('2600')) return 'forskuddstrekk';
  if (text.startsWith('2770')) return 'aga';
  if (text.startsWith('2780') || text.startsWith('2771')) return 'aga_feriepenger';
  if (text.startsWith('294')) return 'feriepenger';
  if (text.startsWith('293') || text.startsWith('292')) return 'skyldig_lonn';
  if (text.startsWith('2750') || text.startsWith('5950')) return 'pensjon';
  return null;
};

// Rollekodene er stabile og brukes til oppslag av grunnlag lenger opp
// (`roleBasis`); etiketten oversettes og leses ved bruk, ikke frosset ved
// modul-innlasting. `PAY_RECON_ROLE_LABEL` beholder sin indekserbare form for
// eksisterende forbrukere.
const payReconRoleLabel = (role) => t(`pay.recon.role.${role}`, String(role || ''));
const PAY_RECON_ROLE_LABEL = new Proxy({}, {
  get: (target, prop) => (typeof prop === 'string' ? payReconRoleLabel(prop) : undefined),
});


// ------------------------------------------------------------------
// Hele lønnsbildet for én periode
// ------------------------------------------------------------------
const buildPayrollReconciliationView = ({ periodId, accounts = [] } = {}) => {
  const range = payReconMonthRange(periodId);
  const empty = { periodId, runs: [], sources: [], mainRows: [], controlAccounts: [], employeeItems: [], checks: [], counts: {} };
  if (!range) return empty;

  const state = mergeState(runtime.appState);
  const settings = { ...(state.payrollSettings || {}), country: state.settings?.country, effectiveDate: range.to };
  const allRuns = safeArray(state.payrollRuns);
  const runs = payReconRunsInPeriod(allRuns, range, periodId);
  const postedRuns = runs.filter(isPostedPayrollRun);

  // Lønnssystemets egne tall for perioden, lagt sammen fra linjene. Dette er
  // den ene siden av avstemmingen — hovedboken er den andre.
  const systemTotals = payReconSumTotals(postedRuns.flatMap(
    (run) => safeArray(run.lines).map((line) => payReconLineTotals(line, settings)),
  ));

  // Hovedbokens bevegelse på kontoene byrået har lagt til lønnsområdet.
  const accountCodes = accounts.map((account) => account.accountId).filter(Boolean);
  const ledgerLines = accountCodes.length && typeof buildLedgerLines === 'function'
    ? buildLedgerLines({ from: range.from, to: range.to, accountCodes, state })
    : [];
  const movementByCode = new Map();
  for (const line of ledgerLines) {
    const code = String(line.accountCode || '');
    movementByCode.set(code, payReconRound((movementByCode.get(code) || 0) + num(line.amount)));
  }

  // Lønnskostnaden slik hovedboken ser den. Resultatkontoene ligger ikke i
  // lønnsområdet — de er kostnad, ikke balanse — så de hentes fra
  // saldobalansen for samme måned.
  const reports = typeof getReportsModel === 'function'
    ? getReportsModel({ year: range.year, months: [range.monthIndex, range.monthIndex], includeChart: false, includeResult: false })
    : null;
  const trial = new Map();
  for (const group of safeArray(reports?.trial)) {
    for (const account of safeArray(group.accounts)) trial.set(String(account.nr || ''), account);
  }
  // En konto saldobalansen ikke kjenner er «ikke sammenliknet», ikke null. Uten
  // det skillet ville et foretak med en annen kontoplan faatt et falskt avvik
  // paa hver eneste rad — hovedboken hadde «sagt 0» der den egentlig ikke ble
  // spurt.
  const trialMovement = (codes) => {
    const known = codes.filter((code) => trial.has(String(code)));
    if (!known.length) return null;
    return payReconRound(known.reduce((sum, code) => {
      const row = trial.get(String(code));
      return sum + (num(row.ut) - num(row.inn));
    }, 0));
  };
  // Samme skille for kontrollkontoene: en konto som ikke er lagt til omraadet
  // har ingen bevegelse aa vise, og `0` ville sett ut som «ingen bevegelse».
  const ledgerMovement = (code) => (movementByCode.has(code) ? -movementByCode.get(code) : null);

  // Hovedavstemmingen: lønnssystemet mot hovedboken mot A-meldingen mot banken.
  // Der en kilde ikke har tallet, står det `null` — «ikke sammenliknet», ikke
  // «går opp». Samme skille som resten av avstemmingen bruker.
  const reported = postedRuns.filter((run) => run.altinnSent);
  const reportedTotals = payReconSumTotals(reported.flatMap(
    (run) => safeArray(run.lines).map((line) => payReconLineTotals(line, settings)),
  ));
  const allReported = postedRuns.length > 0 && reported.length === postedRuns.length;
  const ameldingValue = (key) => (allReported ? payReconRound(reportedTotals[key] || 0) : null);

  // Utgiftsgodtgjørelser kan ikke lenger leses av en fast kontoliste: kostnaden
  // går på utleggslinjens konto og dokumentert inngående mva på 2710. Raden
  // leser derfor refusjonsradene slik de faktisk ble postert (merket
  // `reimbursement`) — netto kostnad + mva summerer til brutto, samme grunnlag
  // som lønnssystemets tall. Eldre snapshotter uten merket bygges om med samme
  // bygger, som for dem gir den gamle bruttoposteringen.
  const reimbursementPostedFor = (run) => {
    const taggedSum = (rows) => safeArray(rows)
      .filter((row) => row.reimbursement === true)
      .reduce((sum, row) => sum + num(row.debit) - num(row.credit), 0);
    const snapshot = taggedSum(run.payrollPostingRows);
    if (snapshot > 0 || !safeArray(run.lines).length) return snapshot;
    return typeof buildPayrollPostingRows === 'function' ? taggedSum(buildPayrollPostingRows(run, settings)) : 0;
  };
  const allowancesPosted = postedRuns.length
    ? payReconRound(postedRuns.reduce((sum, run) => sum + reimbursementPostedFor(run), 0))
    : null;

  const paidRuns = postedRuns.filter((run) => run.paymentStatus?.paidAt || run.paidAt);
  const bankNet = paidRuns.reduce(
    (sum, run) => sum + safeArray(run.lines).reduce((inner, line) => inner + num(line.netPay), 0), 0,
  );

  // 5092 er feriepengekostnaden og har sin egen rad lenger nede. Ligger den
  // ogsaa i bruttolonnsraden, teller den to ganger og hovedboken ser ut til aa
  // ha ført mer lonn enn lonnssystemet kjenner.
  const mainRows = [
    { key: 'brutto', label: t('pay.recon.row.brutto'), sys: systemTotals.gross || 0, hb: trialMovement(['5000', '5001', '5010', '5090']), am: ameldingValue('gross'), bank: null },
    { key: 'naturalytelser', label: t('pay.recon.row.naturalytelser'), sys: systemTotals.benefits || 0, hb: trialMovement(['5200', '5251', '5280', '5290']), am: ameldingValue('benefits'), bank: null },
    { key: 'trekkpliktige', label: t('pay.recon.row.trekkpliktige'), sys: systemTotals.taxableDeductions || 0, hb: null, am: ameldingValue('taxableDeductions'), bank: null },
    { key: 'godtgjorelser', label: t('pay.recon.row.godtgjorelser'), sys: systemTotals.allowances || 0, hb: allowancesPosted, am: null, bank: null },
    { key: 'forskuddstrekk', label: t('pay.withholdingTax'), sys: systemTotals.tax || 0, hb: ledgerMovement('2600'), am: ameldingValue('tax'), bank: null },
    { key: 'aga_grunnlag', label: t('pay.recon.row.aga_grunnlag'), sys: systemTotals.agaBase || 0, hb: null, am: ameldingValue('agaBase'), bank: null },
    { key: 'aga', label: t('pay.recon.row.aga'), sys: systemTotals.aga || 0, hb: trialMovement(['5400']), am: ameldingValue('aga'), bank: null },
    { key: 'netto', label: t('pay.recon.row.netto'), sys: systemTotals.net || 0, hb: null, am: null, bank: paidRuns.length ? payReconRound(bankNet) : null },
    { key: 'andre_trekk', label: t('pay.recon.row.andre_trekk'), sys: systemTotals.deductions || 0, hb: null, am: null, bank: null },
    { key: 'feriepenger', label: t('pay.recon.row.feriepenger'), sys: systemTotals.holidayAccrual || 0, hb: trialMovement(['5092']), am: null, bank: null },
    { key: 'pensjon', label: t('pay.recon.row.pensjon'), sys: systemTotals.pension || 0, hb: trialMovement(['5950']), am: null, bank: null },
  ].map((row) => {
    const values = [row.sys, row.hb, row.am, row.bank].filter((value) => value !== null && value !== undefined);
    const diff = values.length < 2 ? null : payReconRound(Math.max(...values) - Math.min(...values));
    return {
      ...row,
      sys: payReconRound(row.sys),
      hb: row.hb === null || row.hb === undefined ? null : payReconRound(row.hb),
      diff,
      status: values.length < 2 ? 'ikke_sammenliknet' : Math.abs(diff) <= 0.5 ? 'stemmer' : 'differanse',
    };
  });

  // Kontrollkontoene: bokført saldo mot det lønnssystemet sier kontoen skal
  // inneholde. Rollen avgjør hvilket grunnlag som gjelder — en konto uten
  // rolle har ikke noe beregnet grunnlag, og da er differansen `null`.
  const roleBasis = {
    forskuddstrekk: systemTotals.tax || 0,
    aga: systemTotals.aga || 0,
    aga_feriepenger: payReconRound((systemTotals.holidayAccrual || 0) * num(settings.employerContributionRate, 14.1) / 100),
    feriepenger: systemTotals.holidayAccrual || 0,
    skyldig_lonn: systemTotals.net || 0,
    pensjon: systemTotals.pension || 0,
  };
  const controlAccounts = accounts.map((account) => {
    const role = payReconAccountRole(account.accountId);
    const movement = movementByCode.has(account.accountId)
      ? movementByCode.get(account.accountId)
      : payReconRound(num(account.movement));
    const basis = role ? payReconRound(roleBasis[role]) : null;
    // Gjeldskontoene står med kredit i hovedboken, mens grunnlaget fra
    // lønnssystemet er et positivt beløp. Sammenlikningen skjer derfor på
    // absoluttverdien av bevegelsen — fortegnet er kontoplanens, ikke en feil.
    const compared = basis === null ? null : payReconRound(Math.abs(movement) - basis);
    return {
      accountId: account.accountId,
      accountName: account.accountName || '',
      role,
      roleLabel: role ? PAY_RECON_ROLE_LABEL[role] : '',
      openingBalance: payReconRound(num(account.openingBalance)),
      movement,
      bookedBalance: payReconRound(num(account.bookedBalance)),
      basis,
      difference: compared,
      status: compared === null ? 'ikke_sammenliknet' : Math.abs(compared) <= 0.5 ? 'stemmer' : 'differanse',
    };
  });

  // Datakildene: hva avstemmingen bygger på, og om kilden faktisk finnes.
  // «Mangler» her betyr at grunnlaget ikke er der — ikke at det er feil.
  const sources = [
    {
      key: 'lonnssystem', label: t('pay.recon.source.payrollSystem'),
      status: postedRuns.length ? 'komplett' : 'mangler',
      detail: postedRuns.length
        ? t(postedRuns.length === 1 ? 'pay.recon.source.payrollSystemDetailSingular' : 'pay.recon.source.payrollSystemDetailPlural', { count: postedRuns.length })
        : t('pay.recon.source.payrollSystemDetailEmpty'),
    },
    {
      key: 'hovedbok', label: t('voucher.view.hovedbok'),
      status: ledgerLines.length ? 'komplett' : 'mangler',
      detail: ledgerLines.length
        ? t('pay.recon.source.ledgerDetail', { count: ledgerLines.length })
        : t('pay.recon.source.ledgerDetailEmpty'),
    },
    {
      key: 'amelding', label: t('pay.amelding'),
      status: postedRuns.length === 0 ? 'ikke_relevant' : allReported ? 'komplett' : 'mangler',
      detail: postedRuns.length === 0
        ? t('pay.recon.source.ameldingDetailNone')
        : allReported
          ? t('pay.recon.source.ameldingDetailAllReported', { reported: reported.length, total: postedRuns.length })
          : t('pay.recon.source.ameldingDetailMissing', { missing: postedRuns.length - reported.length, total: postedRuns.length }),
    },
    {
      key: 'bank', label: t('pay.recon.source.bankPayouts'),
      status: postedRuns.length === 0 ? 'ikke_relevant' : paidRuns.length === postedRuns.length ? 'komplett' : 'mangler',
      detail: postedRuns.length === 0
        ? t('pay.recon.source.bankDetailNone')
        : t('pay.recon.source.bankDetailPaid', { paid: paidRuns.length, total: postedRuns.length }),
    },
    {
      key: 'pensjon', label: t('pay.recon.source.pensionBasis'),
      status: (systemTotals.pension || 0) > 0 ? 'komplett' : 'ikke_relevant',
      detail: (systemTotals.pension || 0) > 0
        ? t('pay.recon.source.pensionDetailPresent')
        : t('pay.recon.source.pensionDetailNone'),
    },
  ];

  const lineTotals = (line) => payReconLineTotals(line, settings);
  const runRows = payReconRunRows({ runs, lineTotals, range });
  const employeeItems = payReconEmployeeItems({ runs: postedRuns, state, range, lineTotals });
  const payrollModel = typeof derivePayroll === 'function' ? derivePayroll(state) : { terms: [] };
  const amelding = payReconAmelding({
    runRows, systemTotals,
    employeeCount: new Set(employeeItems.map((item) => item.employeeId)).size,
  });
  const bank = payReconBank({ runRows, range });
  const tax = payReconTaxSection({ systemTotals, terms: payrollModel.terms, range });
  const holiday = payReconHolidayPension({ systemTotals, controlAccounts, settings });

  const flagCount = (flag) => employeeItems.filter((item) => item.flags.includes(flag)).length;
  const counts = {
    runs: runRows.length,
    postedRuns: postedRuns.length,
    lines: employeeItems.length,
    flagged: employeeItems.filter((item) => item.flags.length).length,
    negativNetto: flagCount('negativ_netto'),
    manglerSkattekort: flagCount('mangler_skattekort'),
    sluttetAnsatt: flagCount('sluttet_ansatt'),
    naturalytelse: flagCount('naturalytelse'),
    uvanligEndring: flagCount('uvanlig_endring'),
    manglerAmelding: flagCount('mangler_amelding'),
    muligDuplikat: flagCount('mulig_duplikat'),
    ikkeUtbetalt: flagCount('ikke_utbetalt'),
    mainDeviations: mainRows.filter((row) => row.status === 'differanse').length,
    accountDeviations: controlAccounts.filter((account) => account.status === 'differanse').length,
  };

  // De automatiske kontrollene, med tallet de fant. De krysser ikke av noe i
  // den faglige sjekklista — de sier hva den som krysser av bør ha sett på.
  const checks = [
    { key: 'bokfort', label: t('pay.recon.check.bokfort'), value: runRows.filter((row) => !row.posted).length },
    { key: 'hovedbok', label: t('pay.recon.check.hovedbok'), value: counts.mainDeviations },
    { key: 'kontrollkontoer', label: t('pay.recon.check.kontrollkontoer'), value: counts.accountDeviations },
    { key: 'bank', label: t('pay.recon.check.bank'), value: bank.unpaidRuns },
    { key: 'amelding', label: t('pay.recon.check.amelding'), value: amelding.missingRuns.length },
    { key: 'skattekort', label: t('pay.recon.check.skattekort'), value: counts.manglerSkattekort },
    { key: 'negativ', label: t('pay.recon.check.negativ'), value: counts.negativNetto },
    { key: 'duplikat', label: t('pay.recon.check.duplikat'), value: counts.muligDuplikat },
    { key: 'uvanlig', label: t('pay.recon.check.uvanlig'), value: counts.uvanligEndring },
    { key: 'sluttet', label: t('pay.recon.check.sluttet'), value: counts.sluttetAnsatt },
    { key: 'naturalytelse', label: t('pay.recon.check.naturalytelse'), value: counts.naturalytelse },
    { key: 'feriepenger', label: t('pay.recon.check.feriepenger'),
      value: holiday.difference === null ? 0 : Math.abs(holiday.difference) > 0.5 ? 1 : 0 },
    { key: 'termin', label: t('pay.recon.check.termin'), value: tax.openTerms },
  ];

  return {
    periodId, from: range.from, to: range.to,
    runs: runRows, sources, mainRows, controlAccounts, employeeItems,
    amelding, bank, tax, holiday, systemTotals, counts, checks,
    ledgerLines,
    // Uten kontoer på lønnsområdet finnes ingen hovedbokside å sammenlikne med,
    // og da er kontrollkontoene tomme fordi ingen er lagt til — ikke fordi de
    // stemmer. Skjermen må kunne si hvilken av delene.
    ledgerLinked: accountCodes.length > 0,
  };
};

Object.assign(window, {
  buildPayrollReconciliationView, payReconMonthRange, payReconAccountRole,
  PAY_RECON_ROLE_LABEL, payReconRoleLabel,
});
